import React, { useState } from 'react';

import cx from 'classnames';
import _ from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';
import CSSTransition from 'react-transition-group/CSSTransition';

import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal';

import styles from './BiometricTableRow.module.scss';
import EditMenu from './EditMenu';
import fadeAnimation from '../../../../components/animations/fadeAnimation.module.scss';
import modalStyles from '../../../../components/modalStyles';
import OutsideClickHandler from '../../../../components/OutsideClickHandler';
import TableDataCell from '../../../../components/table/TableDataCell';
import Tooltip from '../../../../components/Tooltip';

const addModalStyle = _.cloneDeep(modalStyles);
addModalStyle.content.width = 'min(640px, 94vw)';
addModalStyle.content.marginTop = '100px';

const BiometricTableRow = ({
  data,
  onSelect,
  onEdit,
  onMakeBillable,
  onDelete,
  onAddNote,
  loadingAction,
  currentActionId,
}) => {
  const [editOpened, setEditOpened] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [action, setAction] = useState('delete');

  const isLoading = currentActionId === data.id && loadingAction;

  const onConfirm = () => {
    setOpenConfirm(false);
    switch (action) {
      case 'billable':
        onMakeBillable(data.id, data.isAddedToUnbilledCharges);
        break;
      default:
        onDelete(data.id);
        break;
    }
  };

  const onOpenConfirm = (ationType) => {
    setOpenConfirm(true);
    setEditOpened(false);
    setAction(ationType);
  };

  return (
    <tr
      key={data.id}
      className={cx(styles.row, 't-row border-btm light')}
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.target === event.currentTarget && event.key === 'Enter') onSelect();
      }}
      onClick={() => {
        if (!loadingAction) onSelect();
      }}
    >
      <TableDataCell className='ps-3'>
        <>
          <div className='d-flex justify-content-start align-items-center'>
            {data.name.length > 15 ? (
              <div className='tooltip-box'>
                {data.name.substring(0, 15)}...
                <Tooltip label={data.name} />
              </div>
            ) : (
              data.name
            )}
            {data.scheduledForDelete && (
              <div className='tooltip-box d-flex align-content-start ms-3'>
                <i className={cx(styles['icon-schedule-for-delete'], 'icon-alert-triangle')} />
                <Tooltip label='Scheduled for delete' />
              </div>
            )}
          </div>
          <small className='d-block'>
            Added to Unbilled Charges:{' '}
            {data.isAddedToUnbilledCharges === true
              ? 'YES'
              : data.isAddedToUnbilledCharges === false
              ? 'NO'
              : 'Unknown'}
          </small>
        </>
      </TableDataCell>
      <TableDataCell>
        <>
          <div className='d-flex justify-content-start align-items-center'>
            {data.serialNumber.length > 15 ? (
              <div className='tooltip-box'>
                {data.serialNumber.substring(0, 15)}...
                <Tooltip label={data.serialNumber} />
              </div>
            ) : (
              data.serialNumber
            )}
          </div>
        </>
      </TableDataCell>
      <TableDataCell>{data.protocol === 'OTHER' ? 'Other' : data.protocol}</TableDataCell>
      <TableDataCell>
        {data.lastImportDate ? moment(data.lastImportDate).format('MMM DD, YYYY  h:mm') : '- -'}
      </TableDataCell>
      <TableDataCell>{data.configuredAt ? moment(data.configuredAt).format('MMM DD, YYYY') : '- -'}</TableDataCell>

      <TableDataCell>
        <>
          <div className='d-flex justify-content-end align-items-center'>
            <div className='position-relative'>
              <div className='w-100 d-flex align-items-center justify-content-end'>
                {isLoading && (
                  <span className='me-2 text-muted small'>
                    <i className='icon-loading icon-spin me-1' />
                    {loadingAction === 'edit'
                      ? 'Editing...'
                      : loadingAction === 'delete'
                      ? 'Deleting...'
                      : loadingAction === 'billable'
                      ? 'Updating...'
                      : loadingAction === 'note'
                      ? 'Adding note...'
                      : 'Processing...'}
                  </span>
                )}
                <button
                  type='button'
                  aria-label={`Actions for ${data.name}`}
                  disabled={!!loadingAction}
                  className={cx(styles.moreBtn, 'icon-more-vertical', isLoading && styles.disabled)}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!isLoading) {
                      if (!loadingAction) setEditOpened(true);
                    }
                  }}
                />
              </div>
              <CSSTransition
                mountOnEnter
                unmountOnExit
                in={editOpened}
                timeout={200}
                classNames={{
                  enter: fadeAnimation.enter,
                  enterActive: fadeAnimation.enterActive,
                  exit: fadeAnimation.exit,
                  exitActive: fadeAnimation.exitActive,
                }}
              >
                <OutsideClickHandler
                  clickHandler={() => {
                    setEditOpened(false);
                  }}
                >
                  <EditMenu
                    deviceId={data.id}
                    isAddedToUnbilledCharges={data.isAddedToUnbilledCharges}
                    onEdit={() => {
                      if (!isLoading) {
                        onEdit(data.id);
                        setEditOpened(false);
                      }
                    }}
                    onPreview={() => {
                      if (!isLoading) {
                        onSelect(data);
                        setEditOpened(false);
                      }
                    }}
                    onDelete={() => {
                      if (!isLoading) {
                        onOpenConfirm('delete');
                      }
                    }}
                    onMakeBillable={() => {
                      if (!isLoading) {
                        onOpenConfirm('billable');
                      }
                    }}
                    onAddNote={() => {
                      if (!isLoading) {
                        onAddNote(data.id);
                        setEditOpened(false);
                      }
                    }}
                    loadingAction={isLoading ? loadingAction : null}
                  />
                </OutsideClickHandler>
              </CSSTransition>
            </div>
          </div>
          <div
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <ConfirmationModal
              isOpen={openConfirm}
              onAction={onConfirm}
              onClose={() => {
                setOpenConfirm(false);
              }}
              description={
                action === 'billable'
                  ? 'Are you sure you want to make this device billable?'
                  : 'Are you sure you want to remove this device?'
              }
              title={action === 'billable' ? 'Make device billable' : 'Remove Device'}
              type={action === 'billable' ? 'info' : 'danger'}
              actionText={action === 'billable' ? 'Make billable' : 'Delete'}
            />

            {/* <Modal
              isOpen={tmpUnbilledModal}
              ariaHideApp={false}
              closeTimeoutMS={300}
              style={addModalStyle}
              onRequestClose={() => {
                setTmpUnbilledModal(false);
              }}
            >
              <TmpAddToUnbilled
                onClose={() => {
                  setTmpUnbilledModal(false);
                }}
              />
            </Modal> */}
          </div>
        </>
      </TableDataCell>
    </tr>
  );
};

BiometricTableRow.propTypes = {
  data: PropTypes.object.isRequired,
  onMakeBillable: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  onAddNote: PropTypes.func.isRequired,
  loadingAction: PropTypes.string,
  currentActionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

BiometricTableRow.defaultProps = {
  loadingAction: null,
  currentActionId: null,
};

export default BiometricTableRow;
